/**
 * Web analog of Swing `ru.esoft.swingfatclient.frames.MainFrame`.
 * Scripts: `MainFrame.getInstance().changeUser(newUserData)`.
 */

import UniData from '@/common/classes/UniData'
import { getCoreType, CoreExecutorType } from '@/common/constants/core'
import { IKey } from '@/common/constants/ikey'
import { ActionError } from '@/common/exceptions'
import { getLocale } from '@/common/i18n'
import { handleSessionExpired } from '@/common/plugins/axios'
import { isMiddleErrorResponse, getMiddleErrorMessage } from '@/common/utils/middleErrorResponse'
import apiService from '@/services/api.service'
import { createSafeClientData } from '@/services/core/core.service'
import { messagesService } from '@/services/messages/messages.service'
import { resetStartupActionSession } from '@/services/startup/startupAction.service'
import { useAppStore } from '@/stores/app'
import { useUserStore } from '@/stores/user'
import { useWebSocketStore } from '@/stores/websocket'
import router from '@/router'

/** AppCode.LOGIN_FAIL_CHANGE_USER — смена отклонена, сессия сохраняется. */
export const CHANGE_USER_FAIL_CODE = 1003

function resolveErrorCode(error: unknown): number | null {
  if (error instanceof ActionError) {
    const n = typeof error.code === 'number' ? error.code : parseInt(String(error.code), 10)
    return Number.isFinite(n) ? n : null
  }
  const ax = error as { response?: { data?: unknown }; workflowDetails?: { code?: unknown } }
  const fromDetails = ax?.workflowDetails?.code
  if (fromDetails !== undefined && fromDetails !== null) {
    const n = typeof fromDetails === 'number' ? fromDetails : parseInt(String(fromDetails), 10)
    if (Number.isFinite(n)) return n
  }
  const data = ax?.response?.data
  if (isMiddleErrorResponse(data)) {
    const n = typeof data.code === 'number' ? data.code : parseInt(String(data.code), 10)
    return Number.isFinite(n) ? n : null
  }
  return null
}

function resolveErrorMessage(error: unknown): string {
  if (error instanceof ActionError) {
    const fromDev = String(error.developerMessage || '').trim()
    if (fromDev) return fromDev
    return String(error.message || '').trim() || 'Выполнение действия невозможно.'
  }
  const ax = error as { response?: { data?: unknown }; message?: string }
  const data = ax?.response?.data
  if (data && typeof data === 'object') {
    const msg = getMiddleErrorMessage(data as Record<string, unknown>)
    if (msg) return msg
  }
  return String(ax?.message || '').trim() || 'Выполнение действия невозможно.'
}

function ensureUniData(newUserData: unknown): UniData {
  if (newUserData instanceof UniData) {
    return newUserData
  }
  if (
    newUserData &&
    typeof newUserData === 'object' &&
    typeof (newUserData as UniData).setField === 'function' &&
    typeof (newUserData as UniData)._getAllFields === 'function'
  ) {
    return newUserData as UniData
  }
  return new UniData(
    newUserData && typeof newUserData === 'object' ? (newUserData as Record<string, any>) : {},
  )
}

function buildChangeUserRequest(newUserData: unknown) {
  const locale = getLocale()
  const uniData = ensureUniData(newUserData)
  uniData.setField(IKey.LANG, locale)

  return {
    type: getCoreType() || CoreExecutorType.WF,
    sessionId: null,
    isClone: false,
    cloneOf: null,
    currentData: createSafeClientData({
      fields: {
        fields: uniData._getAllFields(),
      },
    }),
  }
}

async function reinitAfterChangeUser(platformConfig: Record<string, any>): Promise<void> {
  const appStore = useAppStore()
  const userStore = useUserStore()
  const wsStore = useWebSocketStore()

  appStore.removeAllDialogs()
  appStore.removeAllOverlays()
  appStore.clearPlatformPagesCache({ preserveMainFrame: false })
  appStore.clearUserScopedConfig()
  resetStartupActionSession()

  wsStore.disconnect()

  appStore.applyPlatformConfig(platformConfig)

  await userStore.fetchUserProfile({ force: true })

  if (userStore.token) {
    await wsStore.connect(userStore.token)
  } else {
    await wsStore.connect()
  }

  try {
    await router.push({ name: 'home' })
  } catch {
    // navigation guard / same route — ignore
  }
}

class MainFrameService {
  async changeUser(newUserData: unknown): Promise<boolean> {
    const appStore = useAppStore()
    appStore.setLoading(true, 'Смена пользователя...')

    try {
      const body = buildChangeUserRequest(newUserData)
      const platformConfig = await apiService.userChangeUser(body)
      await reinitAfterChangeUser(platformConfig || {})
      return true
    } catch (error) {
      const code = resolveErrorCode(error)
      if (code === CHANGE_USER_FAIL_CODE) {
        const message = resolveErrorMessage(error)
        await messagesService.showMessageError(message, 'Ошибка!')
        return false
      }

      // После фактической смены middle делает logout (код ≠ 1003) — чистим клиент.
      // Чистый network fail (без envelope) — сессия могла сохраниться, не разлогиниваем.
      if (code != null || error instanceof ActionError) {
        const userStore = useUserStore()
        userStore.logout()
        handleSessionExpired(error)
        return false
      }

      const message = resolveErrorMessage(error)
      await messagesService.showMessageError(message, 'Ошибка!')
      return false
    } finally {
      appStore.setLoading(false)
    }
  }

  reloadClientImports(): void {
    window.dispatchEvent(new CustomEvent('legacy:reload-client-imports'))
  }

  getInfoSystemTitle(): string {
    return document.title || window.location.hostname || ''
  }
}

const mainFrameServiceInstance = new MainFrameService()

/** Packages.ru.esoft.swingfatclient.frames.MainFrame */
export const MainFrame = {
  getInstance() {
    return mainFrameServiceInstance
  },
}

export const mainFrameService = mainFrameServiceInstance

/** @internal exported for unit tests */
export const __test__ = {
  buildChangeUserRequest,
  resolveErrorCode,
  resolveErrorMessage,
  CHANGE_USER_FAIL_CODE,
}
